import { motion } from "framer-motion";
import { ArrowRight, Film, PenLine, Music, BookOpen, Glasses, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n";

// Hero image (Image 1)
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/lkbnhrab_Que%20hacemos.jpeg";

// Background image (Photo 3 - dystopian city)
const BG_IMAGE = "https://customer-assets.emergentagent.com/job_21a64dd8-89a2-456a-af50-8a7c7a229e04/artifacts/np3luf5u_freepik__enhance__35598.png";

const serviceIcons = [Film, PenLine, Music, BookOpen, Glasses, Sparkles];
const serviceKeys = ['direccion', 'guion', 'musica', 'formacion', 'consultoria', 'iaEdits'];

const QueHacemosPage = () => {
  const { t, isEnglish } = useLanguage();
  
  // Build services array from translations
  const services = serviceKeys.map((key, index) => ({
    icon: serviceIcons[index],
    title: t.queHacemos.services[key].title,
    description: t.queHacemos.services[key].description,
  }));
  
  // Process steps from translations
  const processSteps = [
    { step: "01", ...t.queHacemos.process.step1 },
    { step: "02", ...t.queHacemos.process.step2 },
    { step: "03", ...t.queHacemos.process.step3 },
    { step: "04", ...t.queHacemos.process.step4 },
  ];
  
  const contactPath = isEnglish ? "/en/contact" : "/hablemos";

  return (
    <div className="min-h-screen">
      {/* Full-page background image (Photo 3) */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url(${BG_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-background/85" />
      </div>

      {/* Film grain overlay for entire page */}
      <div className="fixed inset-0 pointer-events-none grain-overlay opacity-[0.025] z-10" />

      {/* Content container */}
      <div className="relative z-20 pt-20 lg:pt-24">
        
        {/* Hero Section with Photo 1 */}
        <section className="relative">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="relative w-full"
          >
            <img
              src={HERO_IMAGE}
              alt={t.queHacemos.pageTitle}
              className="w-full h-[50vh] lg:h-[60vh] object-cover"
            />
            {/* Dark gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
            
            {/* Title overlay on the image - same style as "Ficción con IA", "Music Videos" */}
            <div className="absolute bottom-[15%] left-0 w-full">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-foreground drop-shadow-lg"
                    style={{ textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}>
                  {t.queHacemos.pageTitle}
                </h1>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Services Grid */}
        <section className="relative py-16 lg:py-20">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="group p-8 bg-card/30 backdrop-blur-sm border border-border/20 rounded-sm hover:border-border/40 hover:bg-card/50 transition-all duration-500"
                  >
                    <Icon className="w-7 h-7 text-primary/50 mb-6 transition-colors duration-300 group-hover:text-primary/70" strokeWidth={1.5} />
                    <h3 className="font-serif text-xl font-light text-foreground mb-3">
                      {service.title}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="relative py-20 lg:py-28 bg-secondary/20 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-2xl lg:text-3xl font-light text-foreground mb-12 tracking-wide">
                {t.queHacemos.process.title}
              </h2>

              <div className="space-y-8">
                {processSteps.map((item, index) => (
                  <motion.div
                    key={item.step}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex gap-6 items-start"
                  >
                    <span className="text-primary/30 text-sm font-medium mt-1 w-8">
                      {item.step}
                    </span>
                    <div>
                      <h3 className="font-serif text-lg text-foreground mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-24 lg:py-32 bg-background/50 backdrop-blur-sm">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-serif text-2xl lg:text-3xl font-light text-foreground mb-6">
                {t.queHacemos.cta.title}
              </h2>
              <p className="text-muted-foreground mb-10 max-w-lg mx-auto">
                {t.queHacemos.cta.text}
              </p>
              <Link
                to={contactPath}
                className="btn-cinematic inline-flex items-center gap-2 group"
              >
                <span>{t.queHacemos.cta.button}</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default QueHacemosPage;
